import { FunctionComponent, useState, useMemo } from "react";
import DashboardLayout from "../../../components/layout/layout";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Popover,
  FormControlLabel,
  Checkbox,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Skeleton,
  styled,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useGetChannelPartnersUsersList } from "../../../hooks/api/otherextranet";
import { ACTIVE_COLOR, INACTIVE_COLOR } from "../../../components/ui/formInput";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const CheckboxUI = styled(Checkbox)(({ theme }) => ({
  padding: '0px 9px',
  '&.Mui-checked': {
    color: '#c3183c',
  },
}));

export interface UserList {
  id: string;
  company: string;
  firstName: string;
  lastName: string;
  passwordDate: string;
  numberOfLogons: string;
  lastLogon: string;
  email: string;
  phone: string;
  canUpload: boolean,
  canEnterOrders: boolean;
  hasDashboard: boolean,
  hasForecast: boolean,
  forecastEmail: boolean,
  admin: boolean
}

type SortField = 'company' | 'firstName' | 'lastName' | 'passwordDate' | 'numberOfLogons' | 'lastLogon' | 'email' | 'phone';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

const ManageChannelPartnerUserList: FunctionComponent = () => {
  const navigate = useNavigate();
  // Removed pagination state variables
  const { role } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>('');

  
  const roles = [
    { label: 'Show All', key: 'showAll' },
    { label: 'Admins', key: 'admin' },
    { label: 'Up-loaders', key: 'upload' },
    { label: 'Orders', key: 'enterOrders' },
    { label: 'Dashboard', key: 'dashboard' },
    { label: 'Forecast', key: 'forecast' },
  ];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checkboxes, setCheckboxes] = useState({
    showAll: role === 'showAll' ? true : false,
    admin: role === 'admin' ? true : false,
    upload: role === 'upload' ? true : false,
    enterOrders: role === 'enterOrders' ? true : false,
    dashboard: role === 'dashboard' ? true : false,
    forecast: role === 'forecast' ? true : false,
  });

  const getActiveRoleParam = (checkboxes: Record<string, boolean>) => {
    return Object.entries(checkboxes).find(([_, value]) => value === true);
  };

  const activeRole = getActiveRoleParam(checkboxes);
  const { data: channelPartnerList, isFetched: hasFetched, isLoading } = useGetChannelPartnersUsersList(activeRole);

  // Sorting state
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  // Filter users based on search term (firstName, lastName, email, company)
  const filteredUserList = useMemo(() => {
    const list = channelPartnerList || [];
    if (!searchTerm.trim()) {
      return list;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    return list.filter((user: UserList) => {
      const firstName = (user.firstName || '').toLowerCase();
      const lastName = (user.lastName || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const company = (user.company || '').toLowerCase();
      
      return firstName.includes(searchLower) || 
             lastName.includes(searchLower) || 
             email.includes(searchLower) || 
             company.includes(searchLower);
    });
  }, [channelPartnerList, searchTerm]);

  // Sorting logic (removed pagination)
  const sortedUserList = useMemo(() => {
    const list = filteredUserList;
    if (!sortState.field || !sortState.direction) return list;
    return [...list].sort((a, b) => {
      let aValue: any = a[sortState.field!];
      let bValue: any = b[sortState.field!];
      // Date fields
      if (sortState.field === 'passwordDate' || sortState.field === 'lastLogon') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortState.field === 'numberOfLogons') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      if (sortState.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [filteredUserList, sortState]);

  // Sort handler
  const handleSort = (field: SortField) => {
    setSortState(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };
  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortState.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortState.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleCheckboxChange = (key: string) => {
    setCheckboxes((prev) => {
      const updatedState = Object.keys(prev).reduce((acc, k) => {
        acc[k as keyof typeof prev] = false;
        return acc;
      }, {} as typeof prev);

      return {
        ...updatedState,
        [key]: true,
      };
    });
  };

  const navigateDeleteUser = (
    userId: string,
    navigateTo: string,
    firstName: string,
    lastName: string
  ) => {
    if (navigateTo === "delete") {
      const params = new URLSearchParams({
        firstName: firstName,
        lastName: lastName,
      }).toString();
      navigate(`/otherextranet/managecp/delete-user/${userId}?${params}`);
    }
  };

  const navigateEditUser = (
    userId: string,
    navigateTo: string,
    name: string,
    numLogons: string,
    lastLogon: string
  ) => {
    if (navigateTo === "edit") {
      const params = new URLSearchParams({
        name: name,
        numLogons: numLogons,
        lastLogon: lastLogon,
      }).toString();
      navigate(`/otherextranet/managecp/edit-user/${userId}?${params}`);
    }
  };

  // Handle search term change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <DashboardLayout
      titleIcon={<DesktopWindowsOutlinedIcon />}
      title="Other Extranet"
      initialBreadcrumbs="Extranet/Barksdale Admin"
      levelBreadcrumbs=""
      breadcrumbs="Channel Partner Details"
      activeMenu="overview"
      containerTitle={"Manage Channel Partners"}
      scrollableWidth={isLoading ? '100%' : "96%"}
    >
      <Box sx={{ pt: 0, px: 0, pb: 0 }}>
        <Box
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={0}
        >
          <Typography variant="body1" sx={{ fontSize: "1rem" }}>
            User List
          </Typography>

          <IconButton
            onClick={handleClick}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              borderRadius: "12px",
              width: 36,
              height: 36,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Popover Box */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: { p: 2, width: 250 },
          }}
        >
          {/* Checkboxes */}
          <Box display="flex" flexDirection="column" gap={1}>
            {roles.map(({ label, key }) => (
              <FormControlLabel
                key={key}
                control={
                  <CheckboxUI
                    name={key}
                    checked={checkboxes[key as keyof typeof checkboxes]}
                    onChange={() => handleCheckboxChange(key)}
                  />
                }
                label={label}
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleClose}
            >
              Done
            </Button>
          </Box>
        </Popover>
      </Box>

      {/* Search Box */}
      <Box sx={{ mt: 3, mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          placeholder="Search by first name, last name, email, or company"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: searchTerm ? ACTIVE_COLOR : INACTIVE_COLOR }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                  sx={{
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(195, 24, 60, 0.08)',
                    },
                  }}
                  aria-label="clear search"
                >
                  <ClearIcon sx={{ fontSize: 18, color: INACTIVE_COLOR }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          InputLabelProps={{
            sx: {
              color: searchTerm ? ACTIVE_COLOR : INACTIVE_COLOR,
              "&.Mui-focused": {
                color: ACTIVE_COLOR,
              },
              "&.MuiInputLabel-shrink": {
                color: ACTIVE_COLOR,
              },
              "&:hover": {
                color: ACTIVE_COLOR,
              },
            },
          }}
          sx={{
            minWidth: 350,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: ACTIVE_COLOR,
              },
              "&:hover fieldset": {
                borderColor: ACTIVE_COLOR,
              },
            },
          }}
        />
      </Box>

      <Box sx={{ width: "100%", maxWidth: "100%", mt: 3 }}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}
        >
          <Table size="small" aria-label="user list table">
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
              <TableCell>Edit</TableCell>
              <TableCell>Delete</TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('company')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Company
                    <Tooltip title="Click to sort">{getSortIcon('company')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('firstName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    First Name
                    <Tooltip title="Click to sort">{getSortIcon('firstName')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('lastName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Last Name
                    <Tooltip title="Click to sort">{getSortIcon('lastName')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('passwordDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    PW Date
                    <Tooltip title="Click to sort">{getSortIcon('passwordDate')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('numberOfLogons')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    # Logons
                    <Tooltip title="Click to sort">{getSortIcon('numberOfLogons')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('lastLogon')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Last Logon
                    <Tooltip title="Click to sort">{getSortIcon('lastLogon')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('email')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Email
                    <Tooltip title="Click to sort">{getSortIcon('email')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('phone')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Phone
                    <Tooltip title="Click to sort">{getSortIcon('phone')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Upload</TableCell>
                <TableCell>Enter Orders</TableCell>
                <TableCell>Dashboard</TableCell>
                <TableCell>Forecast</TableCell>
                <TableCell>Forecast Email</TableCell>
               
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={16}>
                    <Skeleton width="38%" />
                    <Skeleton width="100%" />
                    <Skeleton width="100%" />
                    <Skeleton width="61%" />
                  </TableCell>
                </TableRow>
              ) : hasFetched && (channelPartnerList?.length === 0 || sortedUserList?.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={16}>
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : sortedUserList?.map((user: UserList, idx: number) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="edit user"
                      onClick={() =>
                        navigateEditUser(
                          user.id,
                          "edit",
                          user.company,
                          user.numberOfLogons,
                          user.lastLogon
                        )
                      }
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="delete user"
                      onClick={() =>
                        navigateDeleteUser(
                          user.id,
                          "delete",
                          user.firstName,
                          user.lastName
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>{user.company}</TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{moment(user.passwordDate).format("MM/DD/YYYY")}</TableCell>
                  <TableCell>{user.numberOfLogons}</TableCell>
                  <TableCell>{user.lastLogon}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.admin && <CheckIcon color="error" />}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.canUpload && <CheckIcon color="error" />}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.canEnterOrders && <CheckIcon color="error" />}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.hasDashboard && <CheckIcon color="error" />}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {user.hasForecast && <CheckIcon color="error" />}
                  </TableCell>
                  <TableCell>{user.forecastEmail}</TableCell>
                  
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
        </TableContainer>
       
        {/* Back Button (removed pagination) */}
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#c3183c", padding: "6px 25px !important" }}
            onClick={() => navigate(-1)}
          >
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ManageChannelPartnerUserList;
